import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { UserCircle } from 'lucide-react-native';

interface ProfileGateProps {
  /** Content to render when profile is complete */
  children: React.ReactNode;
  /** Which fields must be present (default: handle) */
  requiredFields?: ('handle' | 'display_name')[];
}

/**
 * Gates child content behind profile completion.
 * If the user is missing required fields, shows a calm prompt
 * with a button to edit their profile.
 */
export default function ProfileGate({
  children,
  requiredFields = ['handle'],
}: ProfileGateProps) {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();

  const missing: string[] = [];
  for (const field of requiredFields) {
    const value = profile?.[field];
    if (!value || (typeof value === 'string' && !value.trim())) {
      missing.push(field === 'handle' ? 'username' : 'display name');
    }
  }

  if (missing.length === 0) {
    return <>{children}</>;
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <UserCircle size={48} color={theme.colors.textSecondary} strokeWidth={1.5} />
      <Text style={[styles.heading, { color: theme.colors.text }]}>
        Complete your profile
      </Text>
      <Text style={[styles.body, { color: theme.colors.textSecondary }]}>
        A {missing.join(' and ')} {missing.length === 1 ? 'is' : 'are'} required before
        you can use this feature.
      </Text>
      <TouchableOpacity
        style={[styles.button, { backgroundColor: theme.colors.accent }]}
        onPress={() => router.push('/(tabs)/edit-profile')}
        activeOpacity={0.8}
      >
        <Text style={[styles.buttonText, { color: theme.colors.background }]}>
          Edit Profile
        </Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 16,
  },
  heading: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },
  button: {
    marginTop: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 2,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
